import {showCustomsModal} from '../Modals/CustomsModal/index.js'
import {
  updateLabelConfig,
  attachCustomsInfoSelector,
  hasCustomsErrorsSelector,
  hasCustomsWarningSelector,
  customsInfoSelector,
  labelConfigSelector,
  onLabelPropertyValueChange,
  LABEL_PROPERTY_ATTACH_CUSTOMS_INFO,
  isLabelPropertyEnabledSelector,
  LABEL_PROPERTY_INCLUDE_ADDITIONAL_PROPERTIES,
  VALID_ATTACH_CUSTOMS_SHIPPER_TYPES,
} from '../../../data/labelInfos/index.js'
import {getState, useSelector} from '../../../store.js'
import EnableLabelProperty from './EnableLabelProperty.js'
import {formatWeight} from '../../../common/weight.js'
import Currency from '../../../common/components/Currency.js'
import useLabelProperty, {
  defaultValidLabelPropertiesFunc,
  ADDITIONAL_DEPENDENT_PROPERTIES_FUNC,
} from './useLabelProperty.js'
import {useLabelConfigContext} from '../LabelConfigContext.js'
import className from '../../../common/className.js'
import {onCheckboxLabelPropertyEnable} from './ConfigCheckbox.js'

export const LABEL_PROPERTY_CUSTOMS_INFO = 'customs_info'

function attachCustomsInfoLabelPropertiesFunc() {
  return [LABEL_PROPERTY_ATTACH_CUSTOMS_INFO]
}

function customsInfoLabelPropertiesFunc(shipperType, labelInfoID) {
  const labelConfig = labelConfigSelector(getState(), {labelInfoID})

  return labelConfig.attach_customs_info ? [LABEL_PROPERTY_CUSTOMS_INFO] : []
}

function customsInfoDependentPropertiesFunc() {
  return [
    LABEL_PROPERTY_INCLUDE_ADDITIONAL_PROPERTIES,
    LABEL_PROPERTY_ATTACH_CUSTOMS_INFO,
  ]
}

export default function AttachCustomsInfo() {
  const {labelInfoID, shipperType, isLabelEnableMode, isSummary} =
    useLabelConfigContext()
  const [attachCustomsInfoPropertySpecs, customsInfoPropertySpecs] =
    AttachCustomsInfo.propertySpecList
  const [attachCustomsInfoLabelProperty] =
    attachCustomsInfoPropertySpecs.labelPropertiesFunc(shipperType, labelInfoID)
  const [customsInfoLabelProperty] =
    customsInfoPropertySpecs.labelPropertiesFunc(shipperType, labelInfoID)

  const attachCustomsInfo = useSelector(
    (state) => attachCustomsInfoSelector(state, {labelInfoID}) || false,
  )
  const customsInfo = useSelector((state) =>
    customsInfoSelector(state, {labelInfoID}),
  )
  const hasCustomsErrors = useSelector((state) =>
    hasCustomsErrorsSelector(state, {labelInfoID}),
  )
  const hasCustomsWarnings = useSelector((state) =>
    hasCustomsWarningSelector(state, {labelInfoID, shipperType}),
  )
  const isLabelPropertyEnabled = useSelector((state) =>
    isLabelPropertyEnabledSelector(state, {
      labelInfoID,
      labelProperty: attachCustomsInfoLabelProperty,
    }),
  )
  const isLinesLabelPropertyEnabled = useSelector((state) =>
    isLabelPropertyEnabledSelector(state, {
      labelInfoID,
      labelProperty: customsInfoLabelProperty,
    }),
  )

  const disabled = isLabelEnableMode && !isLabelPropertyEnabled
  const disabledLines = isLabelEnableMode && !isLinesLabelPropertyEnabled

  // attach customs info
  useLabelProperty({
    labelProperty: attachCustomsInfoLabelProperty,
    propertySpecs: attachCustomsInfoPropertySpecs,
  })

  const isAttachCustomsInfoLabelPropertyEnabled = useSelector((state) =>
    isLabelPropertyEnabledSelector(state, {
      labelInfoID,
      labelProperty: attachCustomsInfoLabelProperty,
    }),
  )

  // customs info
  useLabelProperty({
    labelProperty: customsInfoLabelProperty,
    propertySpecs: customsInfoPropertySpecs,
  })

  const isCustomsInfoLabelPropertyEnabled = useSelector((state) =>
    isLabelPropertyEnabledSelector(state, {
      labelInfoID,
      labelProperty: customsInfoLabelProperty,
    }),
  )

  if (!attachCustomsInfoPropertySpecs.validShipperTypes.includes(shipperType)) {
    return null
  }

  if (
    isSummary &&
    (isAttachCustomsInfoLabelPropertyEnabled ||
      isCustomsInfoLabelPropertyEnabled)
  ) {
    return isCustomsInfoLabelPropertyEnabled
      ? 'Customs Info Data'
      : 'Customs Info'
  }

  return (
    <>
      <li
        className={className`list__item--shipping-options margin-bottom-15 ${{
          error: hasCustomsErrors,
          warning: hasCustomsWarnings && !hasCustomsErrors,
          'property-disabled': disabled,
          'remove-bb': customsInfo.length > 0 && !disabled,
        }}`}
      >
        <div className="wrap--edit-preset-form-input">
          <label className="inline-block margin-right-5 margin-bottom-0">
            <input
              type="checkbox"
              className="margin-right-5 margin-bottom-0 v-align-middle"
              checked={attachCustomsInfo}
              disabled={disabled}
              onChange={(event) => {
                updateLabelConfig(labelInfoID, {
                  [attachCustomsInfoLabelProperty]: event.target.checked,
                })

                onLabelPropertyValueChange(
                  labelInfoID,
                  attachCustomsInfoLabelProperty,
                )
              }}
            />
            <span className="label--config__txt v-align-middle">
              Attach Customs Info
            </span>
          </label>
          {attachCustomsInfo && (
            <button
              className={className`btn btn--link inline-block fs-n1 v-align-base ${{error: hasCustomsErrors}}`}
              type="button"
              onClick={() => showCustomsModal(labelInfoID)}
              disabled={disabled}
            >
              Edit
            </button>
          )}
        </div>
        <EnableLabelProperty labelProperty={attachCustomsInfoLabelProperty} />
      </li>
      {isLabelEnableMode &&
        attachCustomsInfo &&
        isAttachCustomsInfoLabelPropertyEnabled && (
          <li
            className={className`list__item--shipping-options wrap--edit-preset-form-flex-item list__item--customs-lines ${{
              'property-disabled': disabledLines,
            }}`}
          >
            <div className="wrap--edit-preset-form-input label--config__txt">
              <label className="margin-bottom-0">
                <span className="fs-n0">Customs Lines Info</span>
              </label>
              <ul className="list list--square">
                {customsInfo.map(
                  (
                    {description, quantity, weightOZ, weightLB, value, country},
                    index,
                  ) => (
                    <li
                      className="list__item fs-n1 lh-sm margin-bottom-5"
                      key={index}
                    >
                      {description} ({quantity} qty,{' '}
                      {formatWeight({lb: weightLB, oz: weightOZ})},{' '}
                      <Currency value={value} />, {country})
                    </li>
                  ),
                )}
              </ul>
            </div>
            <EnableLabelProperty labelProperty={customsInfoLabelProperty} />
          </li>
        )}
    </>
  )
}

AttachCustomsInfo.propertySpecList = [
  {
    validShipperTypes: VALID_ATTACH_CUSTOMS_SHIPPER_TYPES,
    labelPropertiesFunc: attachCustomsInfoLabelPropertiesFunc,
    dependentPropertiesFunc: ADDITIONAL_DEPENDENT_PROPERTIES_FUNC,
    validLabelPropertiesFunc: defaultValidLabelPropertiesFunc,
    onLabelPropertyEnable: onCheckboxLabelPropertyEnable,
  },
  {
    validShipperTypes: VALID_ATTACH_CUSTOMS_SHIPPER_TYPES,
    labelPropertiesFunc: customsInfoLabelPropertiesFunc,
    dependentPropertiesFunc: customsInfoDependentPropertiesFunc,
    validLabelPropertiesFunc: defaultValidLabelPropertiesFunc,
  },
]
